import loginService from '@/login/login-service'
import _ from 'lodash'
import locationService from '@/locations/locations-service'
import userRolesService from '@/user-roles/user-roles-service'
import { SettingsFactory } from '@/common/utils/SettingsFactory'
import { EventBus, events } from '@/common/EventBus'
import companyService from '@/companies/company-service'

export default {
  namespaced: true,
  state: {
    user: null,
    redirectFrom: null,
    lastKeepAlive: null,
    keepingAlive: false,
    loginPrompt: false,
    changingCompany: false,
    activeLocation: null,
    activeCompany: null,
    companyRoles: [],
    softwareVersion: ''
  },
  mutations: {
    SET_USER (state, user) {
      state.user = user
      state.lastKeepAlive = user ? Date.now() : null
      SettingsFactory.instance.companyId = _.get(user, 'userCompanyId', null)
      // if (user && window.Appcues) {
      //   window.Appcues.identify(user.id, user)
      // }
    },
    SET_COMPANY_ROLES (state, roles) {
      state.companyRoles.splice(0)
      state.companyRoles.push(...roles)
    },
    SET_REDIRECT_FROM (state, redirectFrom) {
      state.redirectFrom = redirectFrom
    },
    SET_LOGIN_PROMPT (state, loginPrompt) {
      state.loginPrompt = loginPrompt
    },
    SET_CHANGING_COMPANY (state, changingCompany) {
      state.changingCompany = changingCompany
    },
    LOCATION_CREATED (state, newLocation) { state.user.accessibleTenants.push(newLocation) },
    LOCATION_DELETED (state, locationId) {
      state.user.accessibleTenants.splice(state.user.accessibleTenants.findIndex(next => next.id === locationId), 1)
    },
    SET_KEEPING_ALIVE (state, keepingAlive) {
      state.keepingAlive = keepingAlive
    },
    SET_ACTIVE_LOCATION (state, activeLocation) {
      state.activeLocation = activeLocation
      SettingsFactory.instance.locationId = _.get(activeLocation, 'id')
    },
    SET_ACTIVE_COMPANY (state, company) {
      state.activeCompany = company
    },
    SET_SOFTWARE_VERSION (state, version) {
      state.softwareVersion = version
    }
  },
  getters: {
    userLocationKey (state) {
      return state.user ? `user_location::${state.user.userEmail}` : ''
    },
    isCompanyLevel (state) {
      return _.isNil(state.activeLocation)
    },
    isGroupLevel (state) {
      return state.activeLocation && state.activeLocation.type === 'GROUP'
    },
    isLocationLevel (state) {
      return !_.isNil(state.activeLocation)
    },
    isGroupLocation (state) {
      return state.activeLocation && !_.isNil(state.activeLocation.groupId)
    }
  },
  actions: {
    async login ({ getters, commit, dispatch }, { email, password, companyId }) {
      const user = await loginService.login(email, password, companyId || loginService.getLastUserCompany(email))
      commit('SET_USER', user)
      commit('SET_SOFTWARE_VERSION', user.softwareVersion || '')
      localStorage.removeItem('companyLevel')
      await dispatch('setActiveLocation', localStorage[getters.userLocationKey])
      await loginService.setLastUserCompany(user.userEmail, user.userCompanyId)
      await dispatch('setActiveCompany', user.userCompanyId)
    },
    async logout ({ commit }) {
      await loginService.logout()
      commit('SET_USER', null)
      commit('SET_REDIRECT_FROM', null)
    },
    async getStatus ({ commit, state, dispatch, getters }, companyId) {
      commit('SET_CHANGING_COMPANY', true)
      try {
        const user = await loginService.getStatus(companyId)
        loginService.setLastUserCompany(user.userEmail, user.userCompanyId)
        commit('SET_USER', user)
        commit('SET_SOFTWARE_VERSION', user.softwareVersion || '')
        SettingsFactory.instance.companyId = _.get(user, 'userCompanyId')
        dispatch('setActiveCompany', SettingsFactory.instance.companyId)

        return user
      } catch (e) {
        commit('SET_USER', null)
        return Promise.reject(e)
      } finally {
        commit('SET_CHANGING_COMPANY', false)
      }
    },
    async keepAlive ({ commit, state, rootState }) {
      try {
        commit('SET_KEEPING_ALIVE', true)
        const user = await loginService.getStatus(state.user.userCompanyId)
        commit('SET_USER', user)
        commit('SET_SOFTWARE_VERSION', user.softwareVersion || '')
      } catch (error) {
        if (error === 401) {
          commit('SET_LOGIN_PROMPT', true)
          EventBus.$emit(events.sessionTimedOut)
        }
      } finally {
        commit('SET_KEEPING_ALIVE', false)
      }
    },
    async setActiveLocation ({ commit, state, getters, dispatch }, locationId) {
      if (!state.activeLocation && _.isEmpty(locationId)) {
        return
      }

      let location
      if (locationId) {
        try {
          location = await locationService.getLocation(state.user.userCompanyId, locationId)
        } catch (e) {
          location = null
        }
      }

      localStorage[getters.userLocationKey] = _.get(location, 'id', '')
      commit('SET_ACTIVE_LOCATION', location)
    },
    restoreActiveLocation ({ dispatch, getters }) {
      return dispatch('setActiveLocation', localStorage[getters.userLocationKey])
    },
    async setActiveCompany ({ commit }, companyId) {
      try {
        const response = await userRolesService.getUserRoles(companyId, false)
        commit('SET_COMPANY_ROLES', response.roles)
        const company = await companyService.getCompany(companyId)
        commit('SET_ACTIVE_COMPANY', company)
      } catch (e) {
        return null
      }
    },
    async sendPasswordReset ({ dispatch, getters }, email) {
      try {
        const result = await loginService.sendPasswordReset(email)
        return result
      } catch (e) {
        return Promise.reject(e)
      }
    }
  }
}
